/**
 * Settings API Service
 * V2 Settings API for Account Tab & App Tab
 */

import { apiClient } from '../client';
import type { ApiError } from '@/lib/types/errors';
import type { Result } from '@/lib/types/result';

// ============================================================
// Type Definitions
// ============================================================

/**
 * Hex color values for accent colors
 */
export type AccentColorHex = `#${string}`;

// ============================================================
// V2 Settings API (Account Tab & App Tab)
// ============================================================

/**
 * Account info response from Phase 1 backend
 */
export interface AccountInfoResponse {
  telegram_id: string;
  phone_number: string;
  display_name: string;
}

/**
 * Account info update request
 */
interface AccountInfoUpdateRequest {
  display_name: string;
}

/**
 * Display name availability check response
 */
export interface DisplayNameAvailabilityResponse {
  available: boolean;
}

/**
 * Theme settings response from Phase 1 backend
 */
export interface ThemeSettingsResponse {
  theme: 'light' | 'dark';
  accent_color: AccentColorHex;
}

/**
 * Theme settings update request (V2 only - no pastel themes)
 */
interface ThemeSettingsUpdateRequest {
  theme?: 'light' | 'dark';
  accent_color?: AccentColorHex;
}

/**
 * V2 Settings API methods for Account tab and App tab
 */
export const settingsV2Api = {
  // ========== Account Tab Methods ==========

  /**
   * Get account info from Phase 1 backend
   * GET /v2/me/account-info
   */
  async getAccountInfo(): Promise<Result<AccountInfoResponse, ApiError>> {
    return apiClient.get<AccountInfoResponse>('/v2/me/account-info');
  },

  /**
   * Update account info (display name only)
   * PATCH /v2/me/account-info
   */
  async updateAccountInfo(displayName: string): Promise<Result<AccountInfoResponse, ApiError>> {
    const payload: AccountInfoUpdateRequest = {
      display_name: displayName,
    };
    return apiClient.patch<AccountInfoResponse>('/v2/me/account-info', payload);
  },

  /**
   * Check display name availability
   * GET /v2/me/account-info/check-availability?displayName={value}
   * Callers debounce input and may pass `signal` to abort a superseded check.
   */
  async checkDisplayNameAvailability(
    displayName: string,
    options?: { signal?: AbortSignal }
  ): Promise<Result<DisplayNameAvailabilityResponse, ApiError>> {
    return apiClient.get<DisplayNameAvailabilityResponse>(
      '/v2/me/account-info/check-availability',
      {
        params: { displayName },
        signal: options?.signal,
      }
    );
  },

  // ========== App Tab Methods ==========

  /**
   * Get theme settings from Phase 1 backend
   * GET /v2/me/settings
   */
  async getSettings(): Promise<Result<ThemeSettingsResponse, ApiError>> {
    return apiClient.get<ThemeSettingsResponse>('/v2/me/settings');
  },

  /**
   * Update theme and/or accent color (V2 only - no pastel themes)
   * PATCH /v2/me/settings
   */
  async updateThemeSettings(
    theme: 'light' | 'dark',
    accent_color: AccentColorHex
  ): Promise<Result<ThemeSettingsResponse, ApiError>> {
    const payload: ThemeSettingsUpdateRequest = { theme, accent_color };
    return apiClient.patch<ThemeSettingsResponse>('/v2/me/settings', payload);
  },

  async completeOnboarding(): Promise<
    Result<{ learner_onboarding_completed_at: string }, ApiError>
  > {
    return apiClient.post<{ learner_onboarding_completed_at: string }>(
      '/v2/me/onboarding/complete'
    );
  },
};
